import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";
import { Card } from "primereact/card";
import { MultiSelect } from "primereact/multiselect";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../../custom/customDropdown";
import { AuthContext } from "../../../Header/auth - context";
import useVisinaTabele from "../../../custom/visinaTabele";

/* ═══════════════════════════════════════════════════════════════════════════
   Ulazno knjižno odobrenje (KO) i zaduženje (KZ) — dokument DOBAVLJAČA.

   Model je preuzet iz CYCLE-a i usklađen sa SEF-om i Zakonom o PDV (čl. 21/31):
     - poseban dokument sa svojim delovodnim brojem (UKO / UKZ), ne polje fakture
     - iznosi su pozitivni; KO umanjuje, KZ uvećava dug prema dobavljaču
     - ispravlja JEDNU ILI VIŠE ulaznih faktura istog dobavljača; iznos po
       fakturi je opcion — bez njega se deli srazmerno bruto iznosima
     - stavke nose vrstu/podvrstu troška i povrat PDV-a, kao faktura, jer KO
       ispravlja i trošak i prethodni porez (KO se mora iskazati u periodu u
       kom je primljen)
     - razlog izmene je obavezan
   KO se ne plaća kroz izvod — umanjuje otvoreni iznos fakture (vw_FakturaSaldo).
   ═══════════════════════════════════════════════════════════════════════════ */

const KO = 381;
const KZ = 383;

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const iznosTekst = (v) =>
    Number(v || 0).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prazanDokument = {
    id: null,
    NasBroj: "",
    TipDokumenta: KO,
    id_klijent: null,
    refNo: "",
    datum: null,
    dpu: null,
    devizna: false,
    valuta: null,
    kurs: null,
    iznos: null,
    napomena: "",
};

const praznaStavka = {
    id: null,
    id_vrsta_troska: null,
    id_podvrsta_troska: null,
    netoIznos: null,
    stopaPDV: 20,
    pdvIznos: null,
    brutoIznos: null,
    povratPDV: true,
    opis: "",
};

const UlazniKOKZ = ({ sefKokz = null, naUnesen, saFakture = null }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((state) => state.company);
    const toast = useRef(null);

    const [sifarnici, setSifarnici] = useState({});
    const [dok, setDok] = useState({ ...prazanDokument });
    const [poslato, setPoslato] = useState(false);
    const [salje, setSalje] = useState(false);

    const [kandidati, setKandidati] = useState([]);   // ulazne fakture dobavljača
    const [veze, setVeze] = useState([]);             // [{ id_faktura, iznos, ...saldo }]
    const [stavke, setStavke] = useState([]);

    const [pregled, setPregled] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);

    const [stavkaVidljiva, setStavkaVidljiva] = useState(false);
    const [stavka, setStavka] = useState({ ...praznaStavka });
    const [stavkaPoslata, setStavkaPoslata] = useState(false);
    const [izabranaStavka, setIzabranaStavka] = useState(null);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const posalji = async (step, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/ulazne", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ step, ...telo }),
        });
        const tekst = await r.text();
        const podaci = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(podaci.message || t("common.greska"));
        return podaci;
    };

    const izmeni = (polje, vrednost) => setDok((p) => ({ ...p, [polje]: vrednost }));

    const ucitajPregled = () =>
        posalji("GET_KOKZ_PREGLED").then((r) => setPregled(Array.isArray(r) ? r : [])).catch(() => {});

    useEffect(() => {
        posalji("INIT").then(setSifarnici).catch((e) => poruka("error", t("common.greska"), e.message));
        ucitajPregled();
        // eslint-disable-next-line
    }, []);

    /* Kandidati za vezu: ulazne fakture izabranog dobavljača. */
    useEffect(() => {
        if (!dok.id_klijent) { setKandidati([]); return; }
        posalji("GET_KOKZ_FAKTURE", { id_klijent: dok.id_klijent })
            .then((r) => setKandidati(Array.isArray(r) ? r : []))
            .catch(() => setKandidati([]));
        // eslint-disable-next-line
    }, [dok.id_klijent]);

    /* Popunjavanje iz SEF dokumenta tipa KO/KZ izabranog u „Fakture za unos". */
    useEffect(() => {
        if (!sefKokz) return;
        const spisak = sifarnici.DobavljaciDB || [];
        if (!spisak.length) return;
        const ocisti = (x) => String(x || "").replace(/[\s-]/g, "");
        const nadjen = spisak.find((d) =>
            (sefKokz.SupplierVat && ocisti(d.pib) === ocisti(sefKokz.SupplierVat)) ||
            (sefKokz.SupplierMB && ocisti(d.maticniBroj) === ocisti(sefKokz.SupplierMB)));
        const devizni = !!sefKokz.Currency && sefKokz.Currency !== "RSD";
        const valuta = (sifarnici.ValuteDB || []).find((v) => v.label === sefKokz.Currency);
        setDok({
            ...prazanDokument,
            TipDokumenta: sefKokz.DocumentType === "DebitNote" ? KZ : KO,
            id_klijent: nadjen ? nadjen.value : null,
            refNo: sefKokz.DocumentNumber || "",
            datum: sefKokz.SentDate ? new Date(sefKokz.SentDate) : null,
            dpu: sefKokz.DeliveryDate ? new Date(sefKokz.DeliveryDate) : null,
            devizna: devizni,
            valuta: devizni && valuta ? valuta.value : null,
            iznos: sefKokz.Amount != null ? Math.abs(Number(sefKokz.Amount)) : null,
        });
        setVeze([]); setStavke([]); setSelectedId(null);
        if (!nadjen && sefKokz.SupplierVat)
            poruka("warn", t("common.upozorenje"), t("sefUlazne.dobavljacNijeNadjen", { pib: sefKokz.SupplierVat }));
        // eslint-disable-next-line
    }, [sefKokz, sifarnici.DobavljaciDB]);

    /* Pun SEF dokument (UBL): datum izdavanja i prometa, razlog (napomena),
       iznos i — najvažnije — fakture na koje se KO/KZ poziva (BillingReference).
       Te fakture se same vezuju čim se učitaju fakture dobavljača. */
    const [sefReference, setSefReference] = useState([]);
    useEffect(() => {
        if (!sefKokz?.id) { setSefReference([]); return; }
        fetch(process.env.REACT_APP_BACKEND_URL + "/sef/UlazniPodaci", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ id: sefKokz.id }),
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((u) => {
                if (!u) return;
                const uDatum = (v) => (v ? new Date(v) : null);
                setDok((p) => ({
                    ...p,
                    TipDokumenta: u.tip === KZ ? KZ : u.tip === KO ? KO : p.TipDokumenta,
                    refNo: u.broj || p.refNo,
                    datum: uDatum(u.datumIzdavanja) || p.datum,
                    dpu: uDatum(u.datumPrometa) || p.dpu,
                    napomena: u.napomena || p.napomena,
                    iznos: u.iznosi?.bruto != null ? Math.abs(Number(u.iznosi.bruto)) : p.iznos,
                }));
                setSefReference(u.reference || []);
            })
            .catch(() => {});
        // eslint-disable-next-line
    }, [sefKokz?.id]);

    useEffect(() => {
        if (!sefReference.length || !kandidati.length || veze.length) return;
        const ocisti = (x) => String(x || "").replace(/s/g, "").toUpperCase();
        const ids = kandidati
            .filter((k) => sefReference.some((r) => ocisti(r.broj) === ocisti(k.BrojDobavljaca)))
            .map((k) => k.value);
        if (ids.length) izaberiFakture(ids);
        else poruka("warn", t("common.upozorenje"), t("ukokz.referenceNisuNadjene",
            { brojevi: sefReference.map((r) => r.broj).join(", ") }));
        // eslint-disable-next-line
    }, [sefReference, kandidati]);

    /* Kurs NBS za devizni dokument — predlog na datum, operater ga sme menjati. */
    useEffect(() => {
        if (!dok.devizna || !dok.valuta || dok.id || dok.kurs) return;
        posalji("GET_KURS", { valuta: dok.valuta, datum: toUtcIso(dok.dpu || dok.datum) })
            .then((r) => { const k = Array.isArray(r) ? r[0] : r; if (k?.Kurs) izmeni("kurs", Number(k.Kurs)); })
            .catch(() => {});
        // eslint-disable-next-line
    }, [dok.devizna, dok.valuta, dok.datum, dok.dpu]);

    const ucitajDokument = async (id) => {
        try {
            const [h] = await posalji("GET_KOKZ_DOKUMENT", { id });
            if (!h) return;
            const rsd = (sifarnici.ValuteDB || []).find((v) => v.value === h.valuta)?.label === "RSD";
            setDok({
                id: h.id, NasBroj: h.NasBroj, TipDokumenta: h.TipDokumenta, id_klijent: h.id_klijent,
                refNo: h.refNo || "", datum: h.datum ? new Date(h.datum) : null, dpu: h.dpu ? new Date(h.dpu) : null,
                devizna: !rsd, valuta: rsd ? null : h.valuta, kurs: rsd ? null : h.kurs,
                iznos: h.iznos, napomena: h.napomena || "",
            });
            const v = await posalji("GET_KOKZ_VEZE", { id });
            setVeze((Array.isArray(v) ? v : []).map((x) => ({ ...x, iznos: x.Iznos })));
            const s = await posalji("GET_KOKZ_STAVKE", { id });
            setStavke(Array.isArray(s) ? s : []);
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        }
    };

    const novi = () => {
        setDok({ ...prazanDokument }); setVeze([]); setStavke([]); setPoslato(false); setSelectedId(null);
    };

    /* Izaberi — toggle preko dugmeta (bez onRowClick, vidi CLAUDE.md). */
    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId((prev) => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) ucitajDokument(next);
            else novi();
            return next;
        });
    };

    /* Pokrenuto sa ulazne fakture (tab KO / KZ): nov dokument za tog dobavljača
       sa tom fakturom već izabranom, ili otvaranje postojećeg dokumenta. Čeka
       šifarnik — ucitajDokument po njemu prepoznaje dinarsku valutu. */
    const [cekaFakturu, setCekaFakturu] = useState(null);
    /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
    const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([pregled]);
    useEffect(() => {
        if (!saFakture || !(sifarnici.DobavljaciDB || []).length) return;
        if (saFakture.otvori) { setSelectedId(saFakture.otvori); ucitajDokument(saFakture.otvori); return; }
        if (saFakture.zaFakturu) {
            setDok({ ...prazanDokument, id_klijent: saFakture.zaFakturu.id_klijent });
            setVeze([]); setStavke([]); setPoslato(false); setSelectedId(null);
            setCekaFakturu(saFakture.zaFakturu.id_faktura);
        }
        // eslint-disable-next-line
    }, [saFakture, sifarnici.DobavljaciDB]);
    useEffect(() => {
        if (!cekaFakturu || !kandidati.length) return;
        if (kandidati.some((k) => k.value === cekaFakturu)) izaberiFakture([cekaFakturu]);
        setCekaFakturu(null);
        // eslint-disable-next-line
    }, [cekaFakturu, kandidati]);

    /* Izbor faktura: zadržava upisane iznose za fakture koje ostaju izabrane. */
    const izaberiFakture = (ids) => {
        setVeze((stare) => ids.map((id) => {
            const stara = stare.find((x) => x.id_faktura === id);
            if (stara) return stara;
            const k = kandidati.find((x) => x.value === id) || {};
            return { id_faktura: id, NasBroj: k.label, BrojDobavljaca: k.BrojDobavljaca, Bruto: k.Bruto, Otvoreno: k.Otvoreno, iznos: null };
        }));
    };

    const zbirStavki = stavke.reduce((s, x) => s + Number(x.brutoIznos || 0), 0);
    const iznosDokumenta = stavke.length ? zbirStavki : Number(dok.iznos || 0);
    const zbirVeza = veze.reduce((s, x) => s + Number(x.iznos || 0), 0);
    const sviIznosiUpisani = veze.length > 0 && veze.every((x) => x.iznos != null);

    const nevalja = (v) => poslato && (v === null || v === undefined || v === "");

    const sacuvaj = async () => {
        setPoslato(true);
        if (!dok.TipDokumenta || !dok.id_klijent || !String(dok.refNo).trim() || !dok.datum || !String(dok.napomena).trim()
            || (dok.devizna && (!dok.valuta || !dok.kurs))) {
            poruka("warn", t("common.upozorenje"), t("ukokz.obaveznaPolja"));
            return;
        }
        if (sviIznosiUpisani && Math.abs(zbirVeza - iznosDokumenta) >= 0.01 && iznosDokumenta > 0) {
            poruka("warn", t("common.upozorenje"), t("ukokz.zbirVezaRazlika", { zbir: iznosTekst(zbirVeza), iznos: iznosTekst(iznosDokumenta) }));
            return;
        }
        setSalje(true);
        try {
            const r = await posalji("SAVE_KOKZ", {
                id: dok.id, TipDokumenta: dok.TipDokumenta, id_klijent: dok.id_klijent, refNo: dok.refNo,
                datum: toUtcIso(dok.datum), dpu: toUtcIso(dok.dpu), devizna: dok.devizna ? 1 : 0,
                valuta: dok.valuta, kurs: dok.kurs, iznos: dok.iznos, napomena: dok.napomena,
                veze: veze.map((x) => ({ id_faktura: x.id_faktura, iznos: x.iznos })),
            });
            setDok((p) => ({ ...p, id: r.ID, NasBroj: r.NasBroj || p.NasBroj }));
            setSelectedId(r.ID);
            setPoslato(false);
            poruka("success", t("ukokz.naslov"), r.message);
            ucitajPregled();
            if (sefKokz?.id && r.ID) {
                try {
                    await fetch(process.env.REACT_APP_BACKEND_URL + "/sef/UlazniVeziKoKz", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
                        body: JSON.stringify({ id: sefKokz.id, id_dokument_kokz: r.ID }),
                    });
                    naUnesen?.();
                } catch (_) {
                    poruka("warn", t("common.upozorenje"), t("sefUlazne.vezivanjeNijeUspelo"));
                }
            }
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        } finally {
            setSalje(false);
        }
    };

    const obrisi = async () => {
        if (!dok.id) return;
        try {
            const r = await posalji("DELETE_KOKZ", { id: dok.id });
            poruka("success", t("ukokz.naslov"), r.message);
            novi(); ucitajPregled();
        } catch (e) { poruka("error", t("common.greska"), e.message); }
    };

    const prevuciStavke = async (idFakture) => {
        if (!dok.id) { poruka("warn", t("common.upozorenje"), t("ukokz.prvoSacuvaj")); return; }
        try {
            const r = await posalji("SAVE_KOKZ_PREVUCI", { id_dokument: dok.id, id_faktura: idFakture });
            poruka("success", t("ukokz.naslov"), r.message);
            const s = await posalji("GET_KOKZ_STAVKE", { id: dok.id });
            setStavke(Array.isArray(s) ? s : []);
            ucitajPregled();
        } catch (e) { poruka("error", t("common.greska"), e.message); }
    };

    /* ---- stavke ---- */
    const podvrsteZaVrstu = useMemo(
        () => (sifarnici.PodvrsteTroskaDB || []).filter((p) => !stavka.id_vrsta_troska || p.id_vrsta_troska === stavka.id_vrsta_troska),
        [sifarnici, stavka.id_vrsta_troska]);

    const preracunaj = (s, iPdv) => {
        const neto = Number(s.netoIznos || 0);
        const pdv = iPdv ? Number((neto * Number(s.stopaPDV || 0) / 100).toFixed(2)) : Number(s.pdvIznos || 0);
        return { ...s, pdvIznos: pdv, brutoIznos: Number((neto + pdv).toFixed(2)) };
    };
    const izmeniIznos = (polje, v) => setStavka((p) => preracunaj({ ...p, [polje]: v }, polje !== "pdvIznos"));

    const otvoriStavku = (red) => {
        if (!dok.id) { poruka("warn", t("common.upozorenje"), t("ukokz.prvoSacuvaj")); return; }
        setStavka(red ? { ...praznaStavka, ...red, povratPDV: !!red.povratPDV } : { ...praznaStavka });
        setStavkaPoslata(false);
        setStavkaVidljiva(true);
    };

    const sacuvajStavku = async () => {
        setStavkaPoslata(true);
        if (!stavka.id_vrsta_troska || !stavka.id_podvrsta_troska || !stavka.netoIznos) {
            poruka("warn", t("common.upozorenje"), t("ulazne.stavkaObavezna"));
            return;
        }
        try {
            const r = await posalji("SAVE_KOKZ_STAVKA", { ...stavka, id_dokument: dok.id, povratPDV: stavka.povratPDV ? 1 : 0 });
            poruka("success", t("common.sacuvano"), r.message);
            setStavkaVidljiva(false);
            const s = await posalji("GET_KOKZ_STAVKE", { id: dok.id });
            setStavke(Array.isArray(s) ? s : []);
            ucitajPregled();
        } catch (e) { poruka("error", t("common.greska"), e.message); }
    };

    const obrisiStavku = async () => {
        if (!izabranaStavka?.id) return;
        try {
            const r = await posalji("DELETE_KOKZ_STAVKA", { id: izabranaStavka.id });
            poruka("success", t("common.sacuvano"), r.message);
            setIzabranaStavka(null);
            const s = await posalji("GET_KOKZ_STAVKE", { id: dok.id });
            setStavke(Array.isArray(s) ? s : []);
            ucitajPregled();
        } catch (e) { poruka("error", t("common.greska"), e.message); }
    };

    const nazivIz = (spisak, id) => (sifarnici[spisak] || []).find((x) => x.value === id)?.label || "";

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("KO KZ");
        const headers = [t("nova.fakturaBr"), t("nova.tip"), t("ulazne.dobavljac"), t("ulazne.brojFakture"),
            t("nova.datum"), t("ulazne.iznos"), t("ulazne.valuta"), t("ukokz.brojFaktura"), t("ukokz.razlog")];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (pregled || []).forEach((r) => {
            const row = worksheet.addRow([r.NasBroj, r.Tip, r.Dobavljac, r.BrojDobavljaca, r.Datum || "", r.Iznos, r.Valuta, r.BrojFaktura, r.Napomena]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade" }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `KO KZ ulazni - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={obrisi} data-pr-tooltip={t("ukokz.obrisi")} />}
            </div>
            <input type="search" placeholder={t("nova.phPretraga")} className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    const zaglavljeStavki = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-plus" severity="info" rounded onClick={() => otvoriStavku(null)}
                    data-pr-tooltip={t("ulazne.novaStavka")} />
                <Button type="button" icon="pi pi-minus" severity="danger" rounded disabled={!izabranaStavka}
                    onClick={obrisiStavku} data-pr-tooltip={t("ulazne.obrisiStavku")} />
            </div>
        </div>
    );

    const tipovi = [{ value: KO, label: t("ukokz.ko") }, { value: KZ, label: t("ukokz.kz") }];

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <div className="blagajnaDashboard dnevniceDashboard ulazni-kokz">
                <Card>
                    <div className="gridFakture ulazne-grid">
                        <div className="col-12 md:col-3">
                            <label>{t("nova.fakturaBr")}:</label>
                            <InputText readOnly value={dok.NasBroj} className="ulazne-polje-zakljucano" />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("nova.tip")}: <span className="iz-obavezno">*</span></label>
                            <CustomDropdown name="TipDokumenta" optionLabel="label" value={dok.TipDokumenta}
                                options={tipovi} disabled={!!dok.id}
                                onChange={(e) => izmeni("TipDokumenta", e.target.value)} />
                        </div>
                        <div className="polje-siroko col-12 md:col-6">
                            <label>{t("ulazne.dobavljac")}: <span className="iz-obavezno">*</span></label>
                            <CustomDropdown name="id_klijent" optionLabel="label" value={dok.id_klijent}
                                invalid={nevalja(dok.id_klijent)} options={sifarnici.DobavljaciDB || []}
                                placeholder={t("ulazne.phDobavljac")}
                                onChange={(e) => { izmeni("id_klijent", e.target.value); setVeze([]); }} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("ukokz.brojDokumenta")}: <span className="iz-obavezno">*</span></label>
                            <InputText value={dok.refNo} className={nevalja(dok.refNo) ? "p-invalid" : ""}
                                onChange={(e) => izmeni("refNo", e.target.value)} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("nova.datum")}: <span className="iz-obavezno">*</span></label>
                            <Calendar value={dok.datum} showIcon dateFormat="dd.mm.yy" mask="99.99.9999"
                                className={nevalja(dok.datum) ? "p-invalid" : ""}
                                onChange={(e) => izmeni("datum", e.value)} />
                        </div>
                        <div className="col-12 md:col-3">
                            <label>{t("ukokz.datumIzmene")}:</label>
                            <Calendar value={dok.dpu} showIcon dateFormat="dd.mm.yy" mask="99.99.9999"
                                onChange={(e) => izmeni("dpu", e.value)} />
                        </div>
                        <div className="col-12 md:col-3 ulazne-kvacica" style={{ alignSelf: "end" }}>
                            <Checkbox inputId="ukokzDevizni" checked={!!dok.devizna}
                                onChange={(e) => izmeni("devizna", e.checked)} />
                            <label htmlFor="ukokzDevizni">{t("ulazne.devizna")}</label>
                        </div>
                        {dok.devizna && (
                            <>
                                <div className="col-12 md:col-2">
                                    <label>{t("ulazne.valuta")}: <span className="iz-obavezno">*</span></label>
                                    <CustomDropdown name="valuta" optionLabel="label" value={dok.valuta}
                                        invalid={nevalja(dok.valuta)}
                                        options={(sifarnici.ValuteDB || []).filter((v) => v.label !== "RSD")}
                                        onChange={(e) => izmeni("valuta", e.target.value)} />
                                </div>
                                <div className="col-12 md:col-2">
                                    <label>{t("ulazne.kurs")}: <span className="iz-obavezno">*</span></label>
                                    <InputNumber value={dok.kurs} mode="decimal" minFractionDigits={4} maxFractionDigits={4}
                                        invalid={nevalja(dok.kurs)} onValueChange={(e) => izmeni("kurs", e.value)} />
                                </div>
                            </>
                        )}
                        <div className="col-12 md:col-3">
                            <label>{t("ulazne.iznos")}:</label>
                            <InputNumber value={stavke.length ? zbirStavki : dok.iznos} mode="decimal" minFractionDigits={2}
                                readOnly={stavke.length > 0}
                                inputClassName={stavke.length ? "ulazne-polje-zakljucano" : ""}
                                onValueChange={(e) => izmeni("iznos", e.value)} />
                        </div>
                        <div className="polje-puno col-12">
                            <label>{t("ukokz.razlog")}: <span className="iz-obavezno">*</span></label>
                            <InputTextarea rows={2} value={dok.napomena}
                                className={nevalja(dok.napomena) ? "p-invalid" : ""}
                                onChange={(e) => izmeni("napomena", e.target.value)} />
                        </div>

                        {/* ─── fakture koje se ispravljaju ─── */}
                        <div className="ulazne-red-pun ulazne-podnaslov">{t("ukokz.faktureKojeIspravlja")}</div>
                        <div className="polje-puno col-12">
                            <MultiSelect value={veze.map((x) => x.id_faktura)} options={kandidati}
                                optionLabel="label" optionValue="value" filter display="chip"
                                disabled={!dok.id_klijent} placeholder={t("ukokz.phFakture")}
                                onChange={(e) => izaberiFakture(e.value || [])} style={{ width: "100%" }} />
                        </div>
                    </div>

                    {veze.length > 0 && (
                        <DataTable value={veze} className="izlazneFaktureTable stavke-tabela ukokz-veze"
                            showGridlines size="small">
                            <Column field="NasBroj" header={t("nova.fakturaBr")} />
                            <Column field="BrojDobavljaca" header={t("ulazne.brojFakture")} />
                            <Column header={t("ulazne.bruto")} body={(r) => iznosTekst(r.Bruto)} />
                            <Column header={t("ukokz.otvoreno")} body={(r) => iznosTekst(r.Otvoreno)} />
                            <Column header={t("ukokz.iznosPoFakturi")} style={{ width: "180px" }}
                                body={(r) => (
                                    <InputNumber value={r.iznos} mode="decimal" minFractionDigits={2}
                                        placeholder={veze.length > 1 ? t("ukokz.srazmerno") : t("ukokz.ceoIznos")}
                                        onValueChange={(e) => setVeze((p) => p.map((x) => x.id_faktura === r.id_faktura ? { ...x, iznos: e.value } : x))} />
                                )} />
                            <Column header="" style={{ width: "150px" }}
                                body={(r) => (
                                    <Button type="button" icon="pi pi-copy" label={t("ukokz.prevuciStavke")}
                                        className="p-button-text p-button-sm" disabled={!dok.id}
                                        onClick={() => prevuciStavke(r.id_faktura)} />
                                )} />
                        </DataTable>
                    )}

                    <div className="ulazne-podnaslov">{t("ulazne.stavke")}</div>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable value={stavke} emptyMessage={t("common.noDataTable")}
                        className="izlazneFaktureTable stavke-tabela" showGridlines stripedRows size="small"
                        header={zaglavljeStavki} selectionMode="single" selection={izabranaStavka}
                        onSelectionChange={(e) => setIzabranaStavka(e.value)}
                        onRowDoubleClick={(e) => otvoriStavku(e.data)}>
                        <Column field="rb" header={t("ulazne.rb")} style={{ width: "60px" }} />
                        <Column header={t("ulazne.vrstaTroska")} body={(r) => r.VrstaTroskaNaziv || nazivIz("VrsteTroskaDB", r.id_vrsta_troska)} />
                        <Column header={t("ulazne.podvrstaTroska")} body={(r) => r.PodvrstaTroskaNaziv || nazivIz("PodvrsteTroskaDB", r.id_podvrsta_troska)} />
                        <Column header={t("ulazne.neto")} body={(r) => iznosTekst(r.netoIznos)} />
                        <Column field="stopaPDV" header={t("ulazne.stopaPDV")} />
                        <Column header={t("ulazne.pdv")} body={(r) => iznosTekst(r.pdvIznos)} />
                        <Column header={t("ulazne.bruto")} body={(r) => iznosTekst(r.brutoIznos)} />
                        <Column header={t("ulazne.povratPDV")} body={(r) => (r.povratPDV ? "✓" : "")} style={{ width: "90px" }} />
                    </DataTable>

                    <div className="formUnosZap buttonUnosZap">
                        <button type="button" onClick={sacuvaj} disabled={salje} className="btn btn-success">
                            {dok.id ? t("common.save") : t("admin.unos")}
                        </button>
                    </div>
                </Card>

                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <div ref={omotTabele}>
                    <DataTable value={pregled || []} paginator rows={redovaTabele} globalFilter={globalFilter}
                        scrollable scrollHeight={visinaTabele} header={tableHeader} emptyMessage={t("common.noDataTable")}
                        resizableColumns columnResizeMode="expand" className="turePregledTable"
                        rowClassName={(r) => (String(r.id) === String(selectedId) ? { "selected-row": true } : {})}>
                        <Column header={t("nova.izaberi")} style={{ minWidth: "90px" }}
                            body={(r) => (
                                <Button label={t("nova.izaberi")} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(r)} />
                            )} />
                        <Column field="NasBroj" header={t("nova.fakturaBr")} sortable />
                        <Column field="Tip" header={t("nova.tip")} sortable />
                        <Column field="Dobavljac" header={t("ulazne.dobavljac")} sortable />
                        <Column field="BrojDobavljaca" header={t("ulazne.brojFakture")} />
                        <Column field="Datum" header={t("nova.datum")} />
                        <Column header={t("ulazne.iznos")} body={(r) => iznosTekst(r.Iznos)} />
                        <Column field="Valuta" header={t("ulazne.valuta")} />
                        <Column field="BrojFaktura" header={t("ukokz.brojFaktura")} />
                        <Column field="Napomena" header={t("ukokz.razlog")} />
                    </DataTable>
                    </div>
                </Card>
            </div>

            <Dialog header={t("ulazne.stavkaNaslov")} visible={stavkaVidljiva}
                style={{ width: "50vw" }} breakpoints={{ "1200px": "75vw", "768px": "95vw" }}
                onHide={() => setStavkaVidljiva(false)}>
                <div className="gridFakture ulazne-grid">
                    <div className="col-12 md:col-6">
                        <label>{t("ulazne.vrstaTroska")}: <span className="iz-obavezno">*</span></label>
                        <CustomDropdown name="id_vrsta_troska" optionLabel="label" value={stavka.id_vrsta_troska}
                            options={sifarnici.VrsteTroskaDB || []} invalid={stavkaPoslata && !stavka.id_vrsta_troska}
                            placeholder={t("ulazne.phVrsta")}
                            onChange={(e) => setStavka((p) => ({ ...p, id_vrsta_troska: e.target.value, id_podvrsta_troska: null }))} />
                    </div>
                    <div className="col-12 md:col-6">
                        <label>{t("ulazne.podvrstaTroska")}: <span className="iz-obavezno">*</span></label>
                        <CustomDropdown name="id_podvrsta_troska" optionLabel="label" value={stavka.id_podvrsta_troska}
                            options={podvrsteZaVrstu} disabled={!stavka.id_vrsta_troska}
                            invalid={stavkaPoslata && !stavka.id_podvrsta_troska} placeholder={t("ulazne.phPodvrsta")}
                            onChange={(e) => {
                                /* podrazumevani PDV podvrste — kao na stavci fakture */
                                const v = e.target.value;
                                const d = (sifarnici.PodvrsteTroskaDB || []).find((p) => p.value === v) || {};
                                setStavka((p) => {
                                    const s = { ...p, id_podvrsta_troska: v };
                                    if (d.povratPDV != null) s.povratPDV = d.povratPDV === 1;
                                    return d.stopaPDV != null ? preracunaj({ ...s, stopaPDV: Number(d.stopaPDV) }, true) : s;
                                });
                            }} />
                    </div>
                    <div className="col-12 md:col-3">
                        <label>{t("ulazne.neto")}: <span className="iz-obavezno">*</span></label>
                        <InputNumber value={stavka.netoIznos} mode="decimal" minFractionDigits={2}
                            className={stavkaPoslata && !stavka.netoIznos ? "p-invalid" : ""}
                            onValueChange={(e) => izmeniIznos("netoIznos", e.value)} />
                    </div>
                    <div className="col-12 md:col-2">
                        <label>{t("ulazne.stopaPDV")}:</label>
                        <InputNumber value={stavka.stopaPDV} mode="decimal" maxFractionDigits={2} suffix=" %"
                            onValueChange={(e) => izmeniIznos("stopaPDV", e.value)} />
                    </div>
                    <div className="col-12 md:col-2">
                        <label>{t("ulazne.pdv")}:</label>
                        <InputNumber value={stavka.pdvIznos} mode="decimal" minFractionDigits={2}
                            onValueChange={(e) => izmeniIznos("pdvIznos", e.value)} />
                    </div>
                    <div className="col-12 md:col-3">
                        <label>{t("ulazne.bruto")}:</label>
                        <InputNumber value={stavka.brutoIznos} mode="decimal" minFractionDigits={2} readOnly
                            inputClassName="ulazne-polje-zakljucano" />
                    </div>
                    <div className="col-12 md:col-2 ulazne-kvacica" style={{ alignSelf: "end" }}>
                        <Checkbox inputId="ukokzPovrat" checked={!!stavka.povratPDV}
                            onChange={(e) => setStavka((p) => ({ ...p, povratPDV: e.checked }))} />
                        <label htmlFor="ukokzPovrat">{t("ulazne.povratPDV")}</label>
                    </div>
                    <div className="polje-puno col-12">
                        <label>{t("ulazne.opis")}:</label>
                        <InputTextarea rows={2} value={stavka.opis}
                            onChange={(e) => setStavka((p) => ({ ...p, opis: e.target.value }))} />
                    </div>
                </div>
                <div className="formUnosZap buttonUnosZap">
                    <button type="button" className="btn btn-success" onClick={sacuvajStavku}>
                        {t("common.save")}
                    </button>
                </div>
            </Dialog>
        </div>
    );
};

export default UlazniKOKZ;
